import { useState } from 'react';
import { InlineEdit } from './InlineEdit';
import type { RoomContext } from './RoomView';

const STEPS = [1, 5, 10];
const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0$/, ''));

export function Scoreboard({ ctx }: { ctx: RoomContext }) {
  const { quiz, isHost, send, me } = ctx;
  const [step, setStep] = useState(1);
  const top = Math.max(...quiz.teams.map((t) => t.score));
  const anyScores = quiz.teams.some((t) => t.score !== 0);

  return (
    <section className="scoreboard" aria-label="Scoreboard">
      <div className="section-head">
        <h2>Scores</h2>
        {isHost && (
          <div className="step-picker" role="group" aria-label="Points per click">
            {STEPS.map((s) => (
              <button key={s} aria-pressed={step === s} onClick={() => setStep(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <ol className="score-list">
        {quiz.teams.map((team) => {
          const members = quiz.participants.filter((p) => p.teamId === team.id);
          const huddling = members.some((p) => p.whispering && p.online);
          const leading = anyScores && team.score === top;
          const mine = me?.teamId === team.id;
          return (
            <li key={team.id} className={`score-row${mine ? ' mine' : ''}`} style={{ ['--c' as string]: team.color }}>
              <span className="swatch" aria-hidden />
              <div className="score-name">
                {isHost ? (
                  <InlineEdit value={team.name} label={`${team.name} name`} onSave={(name) => send({ type: 'renameTeam', teamId: team.id, name })} />
                ) : (
                  <span>{team.name}</span>
                )}
                <span className="score-meta">
                  {huddling ? <span className="huddle">huddling</span> : `${members.length} ${members.length === 1 ? 'player' : 'players'}`}
                  {leading && <span className="lead">leading</span>}
                </span>
              </div>
              {isHost ? (
                <div className="score-controls">
                  <button className="pm" aria-label={`Take ${step} from ${team.name}`} onClick={() => send({ type: 'addScore', teamId: team.id, delta: -step })}>
                    −
                  </button>
                  <InlineEdit
                    className="score-num"
                    value={String(team.score)}
                    format={(v) => fmt(Number(v))}
                    inputMode="decimal"
                    label={`${team.name} score`}
                    onSave={(v) => {
                      const n = Number(v.replace(',', '.'));
                      if (Number.isFinite(n)) send({ type: 'setScore', teamId: team.id, score: n });
                    }}
                  />
                  <button className="pm" aria-label={`Give ${step} to ${team.name}`} onClick={() => send({ type: 'addScore', teamId: team.id, delta: step })}>
                    +
                  </button>
                </div>
              ) : (
                <span className="score-num">{fmt(team.score)}</span>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
